/**
 * Jobs Tab — Admin Client Detail
 * View, filter, and act on Job records for a client.
 * Actions: Cancel (Queued), Requeue (Failed/Canceled).
 */
import { useCallback, useEffect, useState } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { useAdminApi } from "@/services/api/admin-api";
import type { JobAdminDetailDto, JobAdminListItemDto } from "@/types/api/admin";

// =============================================================================
// TYPES & CONSTANTS
// =============================================================================

interface JobsTabProps {
    clientId: number;
}

const PAGE_SIZE = 50;

// 10 columns total (ID · Type · Status · File Storage ID · File Name · Document Name · Document ID · Attempts · Created · actions)
const COL_COUNT = 10;

const JOB_TYPE_OPTIONS: SelectItemType[] = [
    { id: "", label: "All types" },
    { id: "OCR", label: "OCR" },
    { id: "Classification", label: "Classification" },
    { id: "Parse", label: "Parse" },
    { id: "Match", label: "Match" },
    { id: "VendorLookup", label: "VendorLookup" },
    { id: "ReceiptLookup", label: "ReceiptLookup" },
    { id: "ExternalExport", label: "ExternalExport" },
];

const STATUS_OPTIONS: SelectItemType[] = [
    { id: "", label: "All statuses" },
    { id: "Queued", label: "Queued" },
    { id: "Processing", label: "Processing" },
    { id: "Complete", label: "Complete" },
    { id: "Failed", label: "Failed" },
    { id: "Canceled", label: "Canceled" },
];

// =============================================================================
// HELPERS
// =============================================================================

function jobStatusColor(status: string): "brand" | "warning" | "success" | "error" | "gray" {
    switch (status) {
        case "Queued":
            return "brand";
        case "Processing":
            return "warning";
        case "Complete":
            return "success";
        case "Failed":
            return "error";
        case "Canceled":
            return "gray";
        default:
            return "gray";
    }
}

function jobTypeColor(jobType: string): "brand" | "gray" {
    switch (jobType) {
        case "ExternalExport":
        case "VendorLookup":
        case "ReceiptLookup":
            return "brand";
        default:
            return "gray";
    }
}

// =============================================================================
// EXPANDED ROW — detail + actions
// =============================================================================

interface ExpandedRowProps {
    clientId: number;
    job: JobAdminListItemDto;
    onActionComplete: () => void;
}

function ExpandedRow({ clientId, job, onActionComplete }: ExpandedRowProps) {
    const adminApi = useAdminApi();
    const [detail, setDetail] = useState<JobAdminDetailDto | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(true);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function fetchDetail() {
            setIsLoadingDetail(true);
            setDetailError(null);
            try {
                const d = await adminApi.getClientJob(clientId, job.id);
                if (!cancelled) setDetail(d);
            } catch (err) {
                if (!cancelled) setDetailError(err instanceof Error ? err.message : "Failed to load details.");
            } finally {
                if (!cancelled) setIsLoadingDetail(false);
            }
        }
        fetchDetail();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId, job.id]);

    const handleCancel = useCallback(async () => {
        setIsSaving(true);
        setActionError(null);
        try {
            await adminApi.cancelClientJob(clientId, job.id);
            onActionComplete();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed to cancel job.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, job.id, onActionComplete]);

    const handleRequeue = useCallback(async () => {
        setIsSaving(true);
        setActionError(null);
        try {
            await adminApi.requeueClientJob(clientId, job.id);
            onActionComplete();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed to requeue job.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, job.id, onActionComplete]);

    const canCancel = job.status === "Queued";
    const canRequeue = job.status === "Failed" || job.status === "Canceled";

    return (
        <tr>
            <td colSpan={COL_COUNT} className="px-4 pt-0 pb-4">
                <div className="border-brand-secondary bg-primary_subtle space-y-4 rounded-lg border px-4 py-4">
                    {/* Detail values */}
                    {isLoadingDetail ? (
                        <LoadingIndicator size="sm" label="Loading details..." />
                    ) : detailError ? (
                        <p className="text-sm text-error-primary">{detailError}</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                            {detail?.idempotencyKey && (
                                <>
                                    <span className="font-medium text-secondary">Idempotency Key</span>
                                    <span className="font-mono text-xs text-primary">{detail.idempotencyKey}</span>
                                </>
                            )}
                            {job.errorMessage && (
                                <>
                                    <span className="font-medium text-secondary">Error Message</span>
                                    <span className="text-error-primary">{job.errorMessage}</span>
                                </>
                            )}
                            {job.errorCategory && (
                                <>
                                    <span className="font-medium text-secondary">Error Category</span>
                                    <span className="text-primary">{job.errorCategory}</span>
                                </>
                            )}
                            {job.errorCode && (
                                <>
                                    <span className="font-medium text-secondary">Error Code</span>
                                    <span className="text-primary">{job.errorCode}</span>
                                </>
                            )}
                            {job.nextRetryAt && (
                                <>
                                    <span className="font-medium text-secondary">Next Retry</span>
                                    <span className="text-primary">{new Date(job.nextRetryAt).toLocaleString()}</span>
                                </>
                            )}
                            {job.completedAt && (
                                <>
                                    <span className="font-medium text-secondary">Completed At</span>
                                    <span className="text-primary">{new Date(job.completedAt).toLocaleString()}</span>
                                </>
                            )}
                            {job.stepExecutionId && (
                                <>
                                    <span className="font-medium text-secondary">Step Execution ID</span>
                                    <span className="text-primary">{job.stepExecutionId}</span>
                                </>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    {(canCancel || canRequeue) && (
                        <div className="flex items-center gap-3 border-t border-secondary pt-3">
                            {canCancel && (
                                <>
                                    <p className="text-sm text-tertiary">Cancel this queued job to prevent it from being processed.</p>
                                    <Button color="secondary" size="sm" onClick={handleCancel} isDisabled={isSaving}>
                                        Cancel Job
                                    </Button>
                                </>
                            )}
                            {canRequeue && (
                                <>
                                    <p className="text-sm text-tertiary">Requeue this job to retry processing from scratch.</p>
                                    <Button color="primary" size="sm" onClick={handleRequeue} isDisabled={isSaving}>
                                        Requeue
                                    </Button>
                                </>
                            )}
                            {isSaving && <LoadingIndicator size="sm" />}
                        </div>
                    )}

                    {actionError && <p className="text-sm text-error-primary">{actionError}</p>}
                </div>
            </td>
        </tr>
    );
}

// =============================================================================
// JOBS TAB
// =============================================================================

export function JobsTab({ clientId }: JobsTabProps) {
    const adminApi = useAdminApi();
    const [jobs, setJobs] = useState<JobAdminListItemDto[]>([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [jobTypeFilter, setJobTypeFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const fetchJobs = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listClientJobs(clientId, {
                jobType: jobTypeFilter || undefined,
                status: statusFilter || undefined,
                pageSize: PAGE_SIZE,
                skip,
            });
            setJobs(res.data);
            setTotal(res.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load jobs.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, jobTypeFilter, statusFilter, skip]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    const handleJobTypeChange = useCallback((key: string | number | null) => {
        setJobTypeFilter(String(key ?? ""));
        setSkip(0);
    }, []);

    const handleStatusChange = useCallback((key: string | number | null) => {
        setStatusFilter(String(key ?? ""));
        setSkip(0);
    }, []);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

    return (
        <div className="space-y-6">
            <section>
                <h3 className="text-lg font-semibold text-primary">Jobs</h3>
                <p className="mt-1 text-sm text-tertiary">View and manage processing jobs for this client.</p>
            </section>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <div className="w-48">
                    <Select label="Job type" items={JOB_TYPE_OPTIONS} selectedKey={jobTypeFilter} onSelectionChange={handleJobTypeChange} size="sm">
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
                <div className="w-44">
                    <Select label="Status" items={STATUS_OPTIONS} selectedKey={statusFilter} onSelectionChange={handleStatusChange} size="sm">
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="py-8">
                    <LoadingIndicator size="sm" label="Loading jobs..." />
                </div>
            ) : error ? (
                <p className="text-sm text-error-primary">{error}</p>
            ) : jobs.length === 0 ? (
                <p className="py-8 text-center text-sm text-quaternary">No jobs found.</p>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-secondary">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-secondary bg-secondary_subtle">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-secondary">ID</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Type</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Status</th>
                                    <th className="px-4 py-3 font-medium text-secondary">File ID</th>
                                    <th className="px-4 py-3 font-medium text-secondary">File Name</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Document ID</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Document Name</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Attempts</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Created</th>
                                    <th className="px-4 py-3 font-medium text-secondary"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary">
                                {jobs.map((job) => {
                                    const isExpanded = expandedId === job.id;
                                    return (
                                        <>
                                            <tr key={job.id} className="hover:bg-primary_hover">
                                                <td className="px-4 py-3 text-tertiary">{job.id}</td>
                                                <td className="px-4 py-3">
                                                    <Badge color={jobTypeColor(job.jobType)} size="sm" type="pill-color">
                                                        {job.jobType}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge color={jobStatusColor(job.status)} size="sm" type="pill-color">
                                                        {job.status}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 text-tertiary">{job.fileStorageId}</td>
                                                <td className="max-w-[180px] truncate px-4 py-3 text-tertiary" title={job.fileName ?? undefined}>
                                                    {job.fileName ?? "—"}
                                                </td>
                                                <td className="px-4 py-3 text-tertiary">{job.documentId ?? "—"}</td>
                                                <td className="max-w-[180px] truncate px-4 py-3 text-tertiary" title={job.documentName ?? undefined}>
                                                    {job.documentName ?? "—"}
                                                </td>
                                                <td className="px-4 py-3 text-tertiary">{job.attemptCount}</td>
                                                <td className="px-4 py-3 text-tertiary">{new Date(job.createdAt).toLocaleDateString()}</td>
                                                <td className="px-4 py-3">
                                                    <Button
                                                        color={isExpanded ? "primary" : "secondary"}
                                                        size="sm"
                                                        onClick={() => setExpandedId(isExpanded ? null : job.id)}
                                                    >
                                                        {isExpanded ? "Close" : "Details"}
                                                    </Button>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <ExpandedRow
                                                    key={`${job.id}-expanded`}
                                                    clientId={clientId}
                                                    job={job}
                                                    onActionComplete={() => {
                                                        setExpandedId(null);
                                                        fetchJobs();
                                                    }}
                                                />
                                            )}
                                        </>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {total > PAGE_SIZE && (
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-tertiary">
                                Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
                            </p>
                            <div className="flex gap-2">
                                <Button color="secondary" size="sm" isDisabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
                                    Previous
                                </Button>
                                <Button color="secondary" size="sm" isDisabled={currentPage >= totalPages} onClick={() => setSkip(skip + PAGE_SIZE)}>
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
